import { create } from 'zustand';
import { temporal } from 'zundo';
import type { Block, BlockInput, ComponentUsage, FontResponse } from '@blogforge/shared';
import { ulid } from 'ulid';
import type { BlockParent, DragPath } from 'src/types';
import { cloneBlocksWithNewIds } from '../helpers.js';

/**
 * Drop a root block id from component-usage metadata; usages left with zero
 * blocks are removed entirely (same policy removeBlock follows).
 */
function pruneComponentUsage(usages: ComponentUsage[], removedBlockId: string): ComponentUsage[] {
  return usages
    .map((u) =>
      u.blockIds.includes(removedBlockId)
        ? { ...u, blockIds: u.blockIds.filter((bid) => bid !== removedBlockId) }
        : u,
    )
    .filter((u) => u.blockIds.length > 0);
}

/**
 * A block on its way into the store, before it has an id.
 *
 * Distributed on purpose: `Omit<Block, 'id'>` collapses the union to the keys
 * every variant shares, which no longer includes `content` now that a list
 * block exists — so a caller could not write the paragraph it is adding.
 */
export type BlockDraft = BlockInput extends infer B
  ? B extends BlockInput
    ? Omit<B, 'id'>
    : never
  : never;

/**
 * Immutably replace `id` inside the (possibly nested) container list described
 * by `path`. Path segments are container ancestors — a `section`'s block list,
 * or the single item template a `list` holds; the target lives in the children
 * of the last segment (or in the root list when the path is empty). Returns
 * { ok: false } when the path/target cannot be resolved, or when a list's slot
 * was handed more than the one block it can hold, so callers can fall back.
 */
function replaceNestedBlock(
  root: Block[],
  path: DragPath,
  id: string,
  replacement: Block[],
): { ok: boolean; blocks: Block[] } {
  const chain: { list: Block[]; container: Block }[] = [];
  let list: Block[] = root;

  for (const seg of path) {
    const idx = list.findIndex((b) => b.id === seg.id);
    const container = idx === -1 ? undefined : list[idx];
    if (!container) return { ok: false, blocks: root };
    if (container.type === 'section') {
      chain.push({ list, container });
      list = (container.attrs.blocks as Block[] | undefined) ?? [];
      continue;
    }
    if (container.type === 'list') {
      const template = container.attrs.itemBlock as Block | undefined;
      chain.push({ list, container });
      list = template ? [template] : [];
      continue;
    }
    return { ok: false, blocks: root };
  }

  const idx = list.findIndex((b) => b.id === id);
  if (idx === -1) return { ok: false, blocks: root };

  let updated: Block[] = [...list];
  updated.splice(idx, 1, ...replacement);

  for (let i = chain.length - 1; i >= 0; i--) {
    const { list: parentList, container } = chain[i]!;
    let rebuilt: Block;
    if (container.type === 'list') {
      // The slot holds one block: a replacement list cannot fit beside it, so
      // the caller is told the swap did not happen rather than losing blocks.
      if (updated.length !== 1) return { ok: false, blocks: root };
      rebuilt = { ...container, attrs: { ...container.attrs, itemBlock: updated[0] } } as Block;
    } else {
      rebuilt = { ...container, attrs: { ...container.attrs, blocks: updated } } as Block;
    }
    updated = parentList.map((b) => (b.id === container.id ? rebuilt : b));
  }

  return { ok: true, blocks: updated };
}

/**
 * Recursively test whether a block id exists anywhere in the tree (root,
 * sections, columns, accordion item sections and list item templates). Used to
 * confirm nested inserts actually landed before committing state.
 */
function containsBlock(blocks: Block[], id: string): boolean {
  for (const b of blocks) {
    if (b.id === id) return true;
    if (b.type === 'section' && containsBlock((b.attrs.blocks as Block[] | undefined) ?? [], id)) {
      return true;
    }
    if (b.type === 'columns') {
      for (const col of (b.attrs.columnBlocks as Block[][] | undefined) ?? []) {
        if (containsBlock(col, id)) return true;
      }
    }
    if (b.type === 'accordion') {
      for (const item of b.attrs.items ?? []) {
        if (item.section && containsBlock([item.section], id)) return true;
      }
    }
    if (b.type === 'list') {
      const template = b.attrs.itemBlock as Block | undefined;
      if (template && containsBlock([template], id)) return true;
    }
  }
  return false;
}

/**
 * Immutably apply `mutate` to the child list resolved by `path`. Handles
 * `section` (attrs.blocks), `columns` (attrs.columnBlocks[column]) and `list`
 * (the one block in attrs.itemBlock, seen as a list of at most one) containers;
 * accordion content keeps its dedicated accordion-aware helpers. Returns
 * { ok: false } when the path cannot be resolved and `changed: false` when the
 * mutation left the list untouched (leaf returned the same array).
 */
function mutateListAt(
  root: Block[],
  path: DragPath,
  mutate: (list: Block[]) => Block[],
): { ok: boolean; blocks: Block[]; changed: boolean } {
  if (path.length === 0) {
    const next = mutate(root);
    return { ok: true, blocks: next, changed: next !== root };
  }

  const [seg, ...rest] = path;
  if (seg === undefined) return { ok: false, blocks: root, changed: false };
  const idx = root.findIndex((b) => b.id === seg.id);
  if (idx === -1) return { ok: false, blocks: root, changed: false };
  const container = root[idx]!;

  if (container.type === 'section') {
    const children = (container.attrs.blocks as Block[] | undefined) ?? [];
    const inner = mutateListAt(children, rest, mutate);
    if (!inner.ok || !inner.changed) return inner;
    const rebuilt = { ...container, attrs: { ...container.attrs, blocks: inner.blocks } } as Block;
    return {
      ok: true,
      changed: true,
      blocks: root.map((b) => (b.id === container.id ? rebuilt : b)),
    };
  }

  if (container.type === 'columns') {
    const col = seg.column ?? 0;
    const columns = (container.attrs.columnBlocks as Block[][] | undefined) ?? [];
    const child = columns[col] ?? [];
    const inner = mutateListAt(child, rest, mutate);
    if (!inner.ok || !inner.changed) return inner;
    const nextColumns = columns.map((c, i) => (i === col ? inner.blocks : c));
    const rebuilt = {
      ...container,
      attrs: { ...container.attrs, columnBlocks: nextColumns },
    } as Block;
    return {
      ok: true,
      changed: true,
      blocks: root.map((b) => (b.id === container.id ? rebuilt : b)),
    };
  }

  if (container.type === 'list') {
    // The item template is the container's whole child list, so it is mutated
    // as a list of at most one: an emptied one clears the slot, and anything a
    // mutation tried to put *beside* the template has nowhere to go — a list
    // holds exactly one (which is why the editor offers no duplicate or
    // add-below for it; see BlockWrapper's `soleBlock`).
    const template = container.attrs.itemBlock as Block | undefined;
    const inner = mutateListAt(template ? [template] : [], rest, mutate);
    if (!inner.ok || !inner.changed) return inner;
    const rebuilt = {
      ...container,
      attrs: { ...container.attrs, itemBlock: inner.blocks[0] },
    } as Block;
    return {
      ok: true,
      changed: true,
      blocks: root.map((b) => (b.id === container.id ? rebuilt : b)),
    };
  }

  return { ok: false, blocks: root, changed: false };
}

/**
 * Immutably append `added` to the child list `path` names: a section's blocks,
 * one column, an accordion item's section, or an empty list item slot (which
 * takes only the first block — a list template is exactly one). The caller
 * checks the result with `containsBlock`, since an unresolved path is a no-op.
 */
function appendAtPath(blocks: Block[], added: Block[], path: DragPath): Block[] {
  if (path.length === 0) {
    return [...blocks, ...added];
  }

  const [next, ...rest] = path;

  return blocks.map((b) => {
    if (b.id !== next?.id) return b;

    if (b.type === 'section') {
      return {
        ...b,
        attrs: {
          ...b.attrs,
          blocks: appendAtPath(b.attrs.blocks as Block[], added, rest),
        },
      };
    }

    if (b.type === 'columns') {
      const colIdx = next?.column ?? 0;
      const columns = (b.attrs.columnBlocks as Block[][] | undefined) ?? [];
      const child = columns[colIdx] ?? [];
      const nextColumns = columns.map((c, i) =>
        i === colIdx ? appendAtPath(child, added, rest) : c,
      );
      return {
        ...b,
        attrs: { ...b.attrs, columnBlocks: nextColumns },
      };
    }

    if (b.type === 'list') {
      const template = b.attrs.itemBlock as Block | undefined;
      if (rest.length === 0) {
        // The slot takes a block while it is empty; once filled, the
        // adder is not offered (the template is one block, see
        // ListRenderer).
        return template || !added[0] ? b : { ...b, attrs: { ...b.attrs, itemBlock: added[0] } };
      }
      if (!template) return b;
      return {
        ...b,
        attrs: { ...b.attrs, itemBlock: appendAtPath([template], added, rest)[0] },
      };
    }

    if (b.type === 'accordion') {
      if (rest.length > 1) {
        throw new Error('Invalid data structure');
      }

      const sectionPath = rest[0];

      if (sectionPath?.type !== 'section') {
        throw new Error('Invalid data structure');
      }

      return {
        ...b,
        attrs: {
          ...b.attrs,
          items: b.attrs.items.map((blockItem) => {
            if (blockItem.section.id === sectionPath.id) {
              return {
                ...blockItem,
                section: {
                  ...blockItem.section,
                  attrs: {
                    ...blockItem.section.attrs,
                    blocks: [...blockItem.section.attrs.blocks, ...added],
                  },
                },
              };
            }

            return blockItem;
          }),
        },
      };
    }

    return b;
  });
}

interface EditorState {
  postId: string | null;
  hoveredBlockId: string | null;
  blocks: Block[];
  fonts: FontResponse[];
  selectedBlockId: [string, DragPath] | null;
  /**
   * Reusable-component usage metadata for the open document. Each entry
   * records where an inserted component instance lives (root block ids), the
   * component version it came from and the snapshot that was inserted — so
   * component updates can be pushed with per-document customization checks.
   */
  componentUsages: ComponentUsage[];

  isDirty: boolean;

  // Actions
  setHoveredBlockId: (id: string | null) => void;
  setPostId: (id: string) => void;
  setBlocks: (blocks: Block[]) => void;
  setComponentUsages: (usages: ComponentUsage[]) => void;
  setFonts: (fonts: FontResponse[]) => void;
  selectBlock: (data: [string, DragPath] | null) => void;
  addBlock: (block: BlockDraft, afterId?: string, path?: DragPath) => void;
  addBlockToSection: (block: BlockDraft, parentBlock: BlockParent, afterId?: string) => void;
  addBlockToPath: (block: BlockDraft, path: DragPath) => void;
  updateBlock: (id: string, patch: Partial<Block>, path: DragPath) => void;
  removeBlock: (id: string, path?: DragPath) => void;
  moveBlock: (activeId: string, overId: string) => void;
  moveBlockInPath: (activeId: string, overId: string, path: DragPath) => void;
  getBlockInPath: (id: string, path: DragPath) => Block | null;
  getBlockIndex: (id: string) => number;
  duplicateBlock: (id: string, path?: DragPath) => void;
  /**
   * Insert a snapshot of a reusable component. At the root (`path` empty) it
   * goes after the selected root block, or at the end; otherwise it is appended
   * to the container `path` names, the same way `addBlockToPath` adds a block.
   */
  insertComponent: (
    component: { id: string; version: number },
    blocks: Block[],
    path?: DragPath,
  ) => void;
  /** Insert freshly-cloned draft blocks at the end of the document and select the first one. */
  applyDraftAppend: (blocks: Block[]) => void;
  /** Replace the whole document with freshly-cloned draft blocks (component usages reset). */
  applyDraftReplaceAll: (blocks: Block[]) => void;
  /** Replace the selected block (root or inside sections) with freshly-cloned draft blocks. */
  applyDraftReplaceSelection: (blocks: Block[]) => boolean;
  markClean: () => void;
  /**
   * Drop the open document entirely (called when the active workspace
   * changes) so no editor keeps rendering — or autosaving — a document
   * that belongs to the workspace being left. `fonts` is kept because it
   * is a global, non-workspace-scoped resource.
   */
  reset: () => void;
}

export const useEditorStore = create<EditorState>()(
  temporal(
    (set, get) => ({
      postId: null,
      blocks: [],
      selectedBlockId: null,
      isDirty: false,
      fonts: [],
      hoveredBlockId: null,
      componentUsages: [],

      getBlockIndex(id) {
        return get().blocks.findIndex((b) => b.id === id);
      },

      setHoveredBlockId: (id) => set({ hoveredBlockId: id }),

      setPostId: (id) => set({ postId: id }),

      setBlocks: (blocks) => set({ blocks, isDirty: false }),

      setComponentUsages: (componentUsages) => set({ componentUsages }),

      selectBlock: (data) => set({ selectedBlockId: data }),

      setFonts: (fonts) => set({ fonts }),

      insertComponent(component, blocks, path = []) {
        const state = get();
        // Clone with fresh ids so every insertion is an independent snapshot.
        const inserted = cloneBlocksWithNewIds(blocks);
        if (inserted.length === 0) return;

        let next: Block[];
        if (path.length > 0) {
          next = appendAtPath(state.blocks, inserted, path);
          if (!containsBlock(next, inserted[0]!.id)) return; // container not found
        } else {
          const [selectedId, selectedPath] = state.selectedBlockId ?? [null, null];
          let insertAt = state.blocks.length;
          if (selectedId && selectedPath && selectedPath.length === 0) {
            const idx = state.blocks.findIndex((b) => b.id === selectedId);
            if (idx !== -1) insertAt = idx + 1;
          }
          next = [...state.blocks];
          next.splice(insertAt, 0, ...inserted);
        }

        const usage: ComponentUsage = {
          id: ulid(),
          componentId: component.id,
          componentVersion: component.version,
          blockIds: inserted.map((b) => b.id),
          snapshot: inserted,
        };

        set({
          blocks: next,
          componentUsages: [...state.componentUsages, usage],
          selectedBlockId: [inserted[inserted.length - 1]!.id, path],
          isDirty: true,
        });
      },

      addBlockToPath(block, path) {
        const newBlock = { ...block, id: ulid() } as Block;

        if (path.length === 0) {
          set((state) => ({
            blocks: [...state.blocks, newBlock],
            selectedBlockId: [newBlock.id, []],
            isDirty: true,
          }));
          return;
        }

        const state = get();
        const next = appendAtPath(state.blocks, [newBlock], path);
        if (!containsBlock(next, newBlock.id)) return; // container not found — nothing added

        set({
          blocks: next,
          selectedBlockId: [newBlock.id, path],
          isDirty: true,
        });
      },

      addBlockToSection(block, parentBlock, afterId) {
        const newBlock = { ...block, id: ulid() } as Block;
        set((state) => {
          const updatedBlocks = state.blocks.map((b) => {
            if (b.id !== parentBlock.id) return b;

            if (b.type === 'section') {
              const nested = b.attrs.blocks as Block[];

              let nextNested: Block[];
              if (!afterId) {
                nextNested = [...nested, newBlock];
              } else {
                const idx = nested.findIndex((nb) => nb.id === afterId);
                nextNested = [...nested];
                nextNested.splice(idx + 1, 0, newBlock);
              }

              return {
                ...b,
                attrs: { ...b.attrs, blocks: nextNested },
              };
            }

            if (
              b.type === 'accordion' &&
              parentBlock.type === 'accordion' &&
              block.type === 'section'
            ) {
              const newItemId = ulid();
              return {
                ...b,
                attrs: {
                  ...b.attrs,
                  items: [
                    ...b.attrs.items,
                    {
                      id: newItemId,
                      section: {
                        id: ulid(),
                        ...block,
                        attrs: { ...block.attrs, reference: newItemId },
                      },
                      title: '',
                    },
                  ],
                },
              } as Block;
            }

            return b;
          });

          return { blocks: updatedBlocks, isDirty: true };
        });
      },

      addBlock: (block, afterId, path = []) => {
        const newBlock = { ...block, id: ulid() } as Block;
        const withBlock = (list: Block[]): Block[] => {
          if (!afterId) return [...list, newBlock];
          const idx = list.findIndex((b) => b.id === afterId);
          if (idx === -1) return list; // afterId not in this list — no-op
          const next = [...list];
          next.splice(idx + 1, 0, newBlock);
          return next;
        };

        const state = get();
        const result = mutateListAt(state.blocks, path, withBlock);
        if (!result.ok || !result.changed) return;
        // A container with no room for the block — a list's item template is
        // one block — leaves the tree unchanged, so nothing is selected either.
        if (!containsBlock(result.blocks, newBlock.id)) return;
        set({
          blocks: result.blocks,
          selectedBlockId: [newBlock.id, path],
          isDirty: true,
        });
      },

      updateBlock: (id, patch, path) => {
        if (path.length === 0) {
          set((state) => ({
            blocks: state.blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as Block) : b)),
            isDirty: true,
          }));
          return;
        }

        const updateInPath = (
          blocks: Block[],
          id: string,
          patch: Partial<Block>,
          path: DragPath,
        ): Block[] => {
          if (path.length === 0) {
            return blocks.map((block) =>
              block.id === id ? ({ ...block, ...patch } as Block) : block,
            );
          }

          const [next, ...rest] = path;

          return blocks.map((block) => {
            if (block.id !== next?.id) return block;

            if (block.type === 'section') {
              return {
                ...block,
                attrs: {
                  ...block.attrs,
                  blocks: updateInPath(block.attrs.blocks as Block[], id, patch, rest),
                },
              };
            }

            if (block.type === 'columns') {
              const colIdx = next?.column ?? 0;
              const columns = (block.attrs.columnBlocks as Block[][] | undefined) ?? [];
              const child = columns[colIdx] ?? [];
              const nextColumns = columns.map((c, i) =>
                i === colIdx ? updateInPath(child, id, patch, rest) : c,
              );
              return {
                ...block,
                attrs: { ...block.attrs, columnBlocks: nextColumns },
              };
            }

            if (block.type === 'list') {
              const template = block.attrs.itemBlock as Block | undefined;
              if (!template) return block;
              return {
                ...block,
                attrs: {
                  ...block.attrs,
                  itemBlock: updateInPath([template], id, patch, rest)[0],
                },
              };
            }

            if (block.type === 'accordion') {
              if (rest.length === 0) {
                throw new Error('Invalid data structure');
              }

              const [sectionPath, ...restOfPath] = rest;

              if (sectionPath?.type !== 'section') {
                throw new Error('Invalid data structure');
              }

              return {
                ...block,
                attrs: {
                  ...block.attrs,
                  items: block.attrs.items.map((blockItem) => {
                    if (blockItem.section.id === sectionPath.id) {
                      return {
                        ...blockItem,
                        section: {
                          ...blockItem.section,
                          attrs: {
                            ...blockItem.section.attrs,
                            blocks: updateInPath(
                              blockItem.section.attrs.blocks as Block[],
                              id,
                              patch,
                              restOfPath,
                            ),
                          },
                        },
                      };
                    }

                    return blockItem;
                  }),
                },
              };
            }

            return block;
          });
        };

        set((state) => ({
          blocks: updateInPath(state.blocks, id, patch, path),
          isDirty: true,
        }));
      },

      getBlockInPath(id: string, path: DragPath): Block | null {
        const walk = (blocks: Block[], path: DragPath): Block | null => {
          if (path.length === 0) {
            return blocks.find((b) => b.id === id) ?? null;
          }

          const [next, ...rest] = path;
          const target = blocks.find((b) => b.id === next?.id);
          if (!target) return null;

          if (target.type === 'section') {
            return walk((target.attrs.blocks as Block[] | undefined) ?? [], rest);
          }

          if (target.type === 'columns') {
            const col = next?.column ?? 0;
            const list = ((target.attrs.columnBlocks as Block[][] | undefined) ?? [])[col] ?? [];
            return walk(list, rest);
          }

          if (target.type === 'list') {
            const template = target.attrs.itemBlock as Block | undefined;
            return walk(template ? [template] : [], rest);
          }

          return null;
        };

        return walk(get().blocks, path);
      },

      removeBlock: (id, path = []) => {
        if (path.length === 0) {
          return set((state) => {
            const blocks = state.blocks.filter((b) => b.id !== id);
            // If a removed block belonged to a component instance, drop it from
            // the instance; drop the whole usage once no blocks remain.
            const componentUsages = state.componentUsages
              .map((u) =>
                u.blockIds.includes(id)
                  ? { ...u, blockIds: u.blockIds.filter((bid) => bid !== id) }
                  : u,
              )
              .filter((u) => u.blockIds.length > 0);
            return {
              blocks,
              componentUsages,
              selectedBlockId: state.selectedBlockId?.[0] === id ? null : state.selectedBlockId,
              isDirty: true,
            };
          });
        }

        // Nested block (section/columns child): remove from its parent list.
        const state = get();
        const result = mutateListAt(state.blocks, path, (list) => list.filter((b) => b.id !== id));
        if (!result.ok || !result.changed) return;
        set({
          blocks: result.blocks,
          // A component instance can live inside a container too.
          componentUsages: pruneComponentUsage(state.componentUsages, id),
          selectedBlockId: state.selectedBlockId?.[0] === id ? null : state.selectedBlockId,
          isDirty: true,
        });
      },

      moveBlock: (activeId, overId) =>
        set((state) => {
          const blocks = [...state.blocks];
          const from = blocks.findIndex((b) => b.id === activeId);
          const to = blocks.findIndex((b) => b.id === overId);
          if (from === -1 || to === -1) return state;
          const [moved] = blocks.splice(from, 1);
          blocks.splice(to, 0, moved!);
          return { blocks, isDirty: true };
        }),

      moveBlockInPath: (activeId, overId, path) =>
        set((state) => {
          const updateNestedChildren = (
            blocks: Block[],
            path: DragPath,
            activeId: string,
            overId: string,
          ): Block[] => {
            const [next, ...rest] = path;

            return blocks.map((block) => {
              if (block.id !== next?.id) return block;

              if (block.type === 'section') {
                if (rest.length === 0) {
                  // This block owns the children being reordered
                  const children = [...(block.attrs.blocks ?? [])] as Block[];
                  const from = children.findIndex((b) => b.id === activeId);
                  const to = children.findIndex((b) => b.id === overId);

                  if (from === -1 || to === -1) return block; // guard

                  const [moved] = children.splice(from, 1);
                  children.splice(to, 0, moved!);

                  return { ...block, attrs: { ...block.attrs, blocks: children } };
                }

                // Keep walking down
                return {
                  ...block,
                  attrs: {
                    ...block.attrs,
                    blocks: updateNestedChildren(
                      block.attrs.blocks as Block[],
                      rest,
                      activeId,
                      overId,
                    ),
                  },
                };
              }

              if (block.type === 'columns') {
                const colIdx = next?.column ?? 0;
                const columns = (block.attrs.columnBlocks as Block[][] | undefined) ?? [];

                if (rest.length === 0) {
                  // Reorder the blocks inside this single column.
                  const children = [...(columns[colIdx] ?? [])];
                  const from = children.findIndex((b) => b.id === activeId);
                  const to = children.findIndex((b) => b.id === overId);

                  if (from === -1 || to === -1) return block; // guard

                  const [moved] = children.splice(from, 1);
                  children.splice(to, 0, moved!);

                  const nextColumns = columns.map((c, i) => (i === colIdx ? children : c));
                  return { ...block, attrs: { ...block.attrs, columnBlocks: nextColumns } };
                }

                // Keep walking down (e.g. into a section nested inside a column)
                const child = columns[colIdx] ?? [];
                const nextColumns = columns.map((c, i) =>
                  i === colIdx ? updateNestedChildren(child, rest, activeId, overId) : c,
                );
                return { ...block, attrs: { ...block.attrs, columnBlocks: nextColumns } };
              }

              if (block.type === 'list') {
                const template = block.attrs.itemBlock as Block | undefined;
                // Nothing of the list's own to reorder — its slot holds one
                // block — but a container inside the template may be the target.
                if (!template || rest.length === 0) return block;
                return {
                  ...block,
                  attrs: {
                    ...block.attrs,
                    itemBlock: updateNestedChildren([template], rest, activeId, overId)[0],
                  },
                };
              }

              return block;
            });
          };

          return {
            blocks: updateNestedChildren(state.blocks, path, activeId, overId),
            isDirty: true,
          };
        }),

      duplicateBlock: (id, path = []) => {
        const state = get();
        const source =
          path.length === 0
            ? state.blocks.find((b) => b.id === id)
            : get().getBlockInPath(id, path);

        if (!source) return;

        // Deep-clone so nested containers (sections/columns/accordions) get
        // fresh ids instead of sharing children with the original.
        const copy = cloneBlocksWithNewIds([source])[0]!;

        if (path.length === 0) {
          // Root: keep using addBlock so behaviour/selection matches before.
          const idx = state.blocks.findIndex((b) => b.id === id);
          if (idx === -1) return;
          const next = [...state.blocks];
          next.splice(idx + 1, 0, copy);
          set({ blocks: next, selectedBlockId: [copy.id, []], isDirty: true });
          return;
        }

        const result = mutateListAt(state.blocks, path, (list) => {
          const idx = list.findIndex((b) => b.id === id);
          if (idx === -1) return list;
          const next = [...list];
          next.splice(idx + 1, 0, copy);
          return next;
        });
        if (!result.ok || !result.changed) return;
        // The copy may have had nowhere to go (see the note on addBlock); a
        // selection pointing at a block that is not in the tree would leave the
        // inspector describing nothing.
        if (!containsBlock(result.blocks, copy.id)) return;
        set({ blocks: result.blocks, selectedBlockId: [copy.id, path], isDirty: true });
      },

      applyDraftAppend: (blocks) => {
        if (blocks.length === 0) return;
        const inserted = cloneBlocksWithNewIds(blocks);
        set((state) => ({
          blocks: [...state.blocks, ...inserted],
          selectedBlockId: [inserted[0]!.id, []],
          isDirty: true,
        }));
      },

      applyDraftReplaceAll: (blocks) => {
        if (blocks.length === 0) return;
        const inserted = cloneBlocksWithNewIds(blocks);
        set({
          blocks: inserted,
          componentUsages: [],
          selectedBlockId: [inserted[0]!.id, []],
          isDirty: true,
        });
      },

      applyDraftReplaceSelection: (blocks) => {
        if (blocks.length === 0) return false;
        const selection = get().selectedBlockId;
        if (!selection) return false;
        const [selectedId, path] = selection;

        const replacement = cloneBlocksWithNewIds(blocks);
        const result = replaceNestedBlock(get().blocks, path, selectedId, replacement);
        if (!result.ok) return false;

        set((state) => ({
          blocks: result.blocks,
          componentUsages:
            path.length === 0
              ? pruneComponentUsage(state.componentUsages, selectedId)
              : state.componentUsages,
          selectedBlockId: [replacement[0]!.id, path],
          isDirty: true,
        }));
        return true;
      },

      markClean: () => set({ isDirty: false }),

      reset: () =>
        set({
          postId: null,
          blocks: [],
          componentUsages: [],
          selectedBlockId: null,
          hoveredBlockId: null,
          isDirty: false,
        }),
    }),
    { limit: 50 }, // 50 undo steps
  ),
);

// Undo / redo helpers
export const useEditorHistory = () => {
  const { undo, redo, pastStates, futureStates } = useEditorStore.temporal.getState();
  return {
    undo,
    redo,
    canUndo: pastStates.length > 0,
    canRedo: futureStates.length > 0,
  };
};
